import type { DateTimeOptions } from 'vue-i18n';

export type Frequency =
  | '1H'
  | 'PT1H'
  | '1D'
  | 'P1D'
  | '10T'
  | 'PT10M'
  | '15T'
  | '5T'
  | '1MS'
  | 'P1M'
  | '1M'
  | 'QS-DEC'
  | '1Y'
  | '1YS'
  | 'P1Y';

export type DisplayFrequency =
  | 'month'
  | 'day';

// For alternate notations, pandas offstrings vs. iso 8701,
// see https://gitlab.geosphere.at/datahub/dataportal/dataset-frontend/-/issues/250

export function maxDate(dates: Date[]): Date {
  return dates.reduce(function (a, b) {
    return a > b ? a : b;
  });
}

export function minDate(dates: Date[]): Date {
  return dates.reduce(function (a, b) {
    return a < b ? a : b;
  });
}

export function localeDateString(
  date: Date,
  locale: string,
  freq: Frequency | DisplayFrequency
): string {
  let extraOptions: { [key: string]: DateTimeOptions };
  let formattedDate: string;

  if (['1D', 'P1D', 'day'].includes(freq)) {
    extraOptions = {
      de: {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      },
      en: {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      },
    };
  } else if (['1M', '1MS', 'P1M', 'month'].includes(freq)) {
    extraOptions = {
      de: {
        month: 'short',
        year: 'numeric',
      },
      en: {
        month: 'short',
        year: 'numeric',
      },
    };
  } else if (['1Y', '1YS', 'P1Y'].includes(freq)) {
    extraOptions = {
      de: {
        year: 'numeric',
      },
      en: {
        year: 'numeric',
      },
    };
  } else {
    extraOptions = {
      de: {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      },
      en: {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      },
    };
  }
  const localeOption: DateTimeOptions | undefined = extraOptions[locale];

  if (['day', 'month'].includes(freq)) {
    // for display frequency, parse date without time
    const [year, month, day] = date.toISOString().split("T")[0].split("-").map(Number);
    const dateOnly = new Date(year, month - 1, day);
    formattedDate = dateOnly.toLocaleDateString(locale, localeOption);
  } else if (freq === 'QS-DEC') {
    const seasonString = getSeasonLabel(monthNumber(date), locale);
    formattedDate = `${seasonString} ${date.getFullYear()}`;
  } else {
    formattedDate = date.toLocaleDateString(locale, localeOption);
    if ("hour" in localeOption || "minute" in localeOption) {
      formattedDate += " UTC"
    }
  }
  return formattedDate;
}

export function getPickerLabel(
  date: Date,
  locale: string,
  type: string
): string {
  let extraOptions: { [key: string]: DateTimeOptions };

  if (type === 'month') {
    extraOptions = {
      de: {
        month: 'short',
      },
      en: {
        month: 'short',
      },
    };
  } else if (type === 'year') {
    extraOptions = {
      de: {
        year: 'numeric',
      },
      en: {
        year: 'numeric',
      },
    };
  } else {
    extraOptions = { de: {}, en: {} }
  }

  if (type === 'season') {
    return getSeasonLabelShort(monthNumber(date), locale);
  } else {
    const localeOption: DateTimeOptions | undefined = extraOptions[locale];
    return date.toLocaleDateString(locale, localeOption);
  }
}

export function addTimezoneOffset(date: Date): Date {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() + offset * 60 * 1000);
}

export function removeTimezoneOffset(date: Date): Date {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000);
}

export function dateDiffInDays(dates: [Date, Date]): number {
  return Math.ceil(
    Math.abs(dates[1].valueOf() - dates[0].valueOf()) / (1000 * 60 * 60 * 24)
  );
}

export function generateRange(
  start: number,
  end: number,
  step: number = 1
): number[] {
  const range: number[] = [];
  for (let i = end; i >= start; i -= step) {
    range.push(i);
  }
  return range;
}

export function dateRangesOverlap(
  range1: [Date, Date],
  range2: [Date, Date]
): boolean {
  return !(range1[1] <= range2[0] || range1[0] >= range2[1]);
}

export function areDateRangesOverlapping(
  rangeA: [Date, Date],
  rangeB: [Date, Date]
): boolean {
  if (rangeB[0] <= rangeA[0]) {
    return rangeB[1] >= rangeA[0];
  } else {
    return rangeB[0] <= rangeA[1];
  }
}

export function getDisplayFrequency(freq: Frequency): DisplayFrequency {
  // freq for displaying start/end date in table and map
  return ['1M', '1MS', 'P1M'].includes(freq) ? 'month' : 'day';
}

export function formatDateForDatepicker(date: Date, freq: Frequency): string {
  let str: string;

  const utcDate = addTimezoneOffset(date);
  const year = utcDate.getFullYear();
  const month = padLeadingZero(utcDate.getMonth() + 1);
  const day = padLeadingZero(utcDate.getDate());

  if (['1M', '1MS', 'P1M'].includes(freq)) {
    str = `${year}-${month}`;
  } else if (['1D', 'P1D'].includes(freq)) {
    str = `${year}-${month}-${day}`;
  } else if (freq === 'QS-DEC') {
    str = `${year}-${month}`;
  } else if (['1Y', '1YS', 'P1Y'].includes(freq)) {
    str = `${year}`;
  } else {
    str = date.toISOString();
  }
  return str;
}

export function formatDateForTextfield(date: Date): string {
  const utcDate = addTimezoneOffset(date);
  const year = utcDate.getFullYear();
  const month = padLeadingZero(utcDate.getMonth() + 1);
  const day = padLeadingZero(utcDate.getDate());
  return `${year}-${month}-${day}`;
}

export function formatDateForFilename(date: Date, freq: Frequency): string {
  let str: string;
  const utcDate = addTimezoneOffset(date);
  const year = utcDate.getFullYear();
  const month = padLeadingZero(utcDate.getMonth() + 1);
  const day = padLeadingZero(utcDate.getDate());
  const hours = padLeadingZero(utcDate.getHours());
  const minutes = padLeadingZero(utcDate.getMinutes());

  if (['1M', '1MS', 'P1M'].includes(freq)) {
    str = `${year}${month}`;
  } else if (['1D', 'P1D'].includes(freq)) {
    str = `${year}${month}${day}`;
  } else if (freq === 'QS-DEC') {
    str = `${year}${month}`;
  } else if (['1Y', '1YS', 'P1Y'].includes(freq)) {
    str = `${year}`;
  } else {
    str = `${year}${month}${day}T${hours}${minutes}`;
  }
  return str;
}

export function formatDateForQuery(date: Date): string {
  return date.toISOString();
}

export function getTimeString(date: Date): string {
  const hours = padLeadingZero(addTimezoneOffset(date).getHours());
  const minutes = padLeadingZero(date.getMinutes());
  return `${hours}:${minutes}`;
}

function padLeadingZero(num: number): string {
  return ('0' + num).slice(-2);
}

export function toIsoDateString(date: Date, separator = '-'): string {
  const str = date.toISOString().substr(0, 10);
  return separator === '-' ? str : str.replace(/-/g, separator);
}
export interface TemporalResolution {
  key: string;
  value: number;
}

export const seasonLabels: Record<string, Record<string, string>> = {
  de: {
    spring: 'Frühling (MÄR-MAI)',
    summer: 'Sommer (JUN-AUG)',
    autumn: 'Herbst (SEP-NOV)',
    winter: 'Winter (DEZ-FEB)',
  },
  en: {
    spring: 'Spring (MAR-MAY)',
    summer: 'Summer (JUN-AUG)',
    autumn: 'Autumn (SEP-NOV)',
    winter: 'Winter (DEC-FEB)',
  },
};

export const seasonLabelsShort: Record<string, Record<string, string>> = {
  de: {
    spring: 'Frühling',
    summer: 'Sommer',
    autumn: 'Herbst',
    winter: 'Winter',
  },
  en: {
    spring: 'Spring',
    summer: 'Summer',
    autumn: 'Autumn',
    winter: 'Winter',
  },
};

export const temporalResolutions: Record<string, TemporalResolution[]> = {
  seasons: [
    { key: 'spring', value: 3 },
    { key: 'summer', value: 6 },
    { key: 'autumn', value: 9 },
    { key: 'winter', value: 12 },
  ],
};

export function getSeasonLabel(value: number, locale: string = 'de'): string {
  const season = temporalResolutions.seasons.find(s => s.value === value)
  return season ? seasonLabels[locale][season.key] : '';
}

export function getSeasonLabelShort(value: number, locale: string = 'de'): string {
  const season = temporalResolutions.seasons.find(s => s.value === value)
  return season ? seasonLabelsShort[locale][season.key] : '';
}

export function formatSeasonLabel(date: Date, locale: string = 'de'): string {
  const month = monthNumber(date);
  const seasonName = getSeasonLabel(month, locale);
  let year = date.getFullYear();

  // Winter spans Dec–Feb, so Jan/Feb belong to previous year
  if (month === 1 || month === 2) {
    year -= 1;
  }

  return `${seasonName} ${year}`;
}

export function monthNumber(date: Date): number {
  return date.getMonth() + 1;
}
